import BigNumber from "bignumber.js";
import { useEffect, useState } from "react";
import { PublicKey } from "@solana/web3.js";
import { useConnection } from "@solana/wallet-adapter-react";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import useLanguage from "@axvdex/hooks/useLanguage";
import imgSanitize from "@axvdex/utils/imgSanitize";
import { DigitalAsset, OutbidSale, SpecificBidAsset } from "@axvdex/types/solana/outbid";
import CustomLoader from "@axvdex/components/common/CustomLoader";
import { fetchDigitalAsset } from "@axvdex/hooks/solana/utils/asset";
import { useAppDispatch } from "@axvdex/state";
import { sendToast } from "@axvdex/state/wallet/walletSlice";
import Button from "../../common/Button";
import { ReactComponent as IcnTwitter } from "../../../assets/icons/icn-twitterx.svg";
import CustomModal from "../../common/CustomModal";

export type SaleIncentiveCongratsModalOpen = {
  open: boolean;
  incentives: DigitalAsset[];
};

type SaleIncentiveCongratsModalProps = {
  saleIncentiveCongratsModalOpen: SaleIncentiveCongratsModalOpen;
  setSaleIncentiveCongratsModalOpen: (open: SaleIncentiveCongratsModalOpen) => void;
  outbidSale: OutbidSale;
};

function SaleIncentiveCongratsModal({
  saleIncentiveCongratsModalOpen,
  setSaleIncentiveCongratsModalOpen,
  outbidSale,
}: SaleIncentiveCongratsModalProps) {
  const { i18 } = useLanguage();
  const [nftImages, setNftImages] = useState<uri[]>([]);
  const [assets, setAssets] = useState<AssetDetail[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { connection } = useConnection();
  const umi = createUmi(connection.rpcEndpoint);

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      const nfts = saleIncentiveCongratsModalOpen.incentives.filter(incentive => incentive.isNFT);
      const assets = saleIncentiveCongratsModalOpen.incentives.filter(incentive => incentive.isAsset);
      getNftDetails(nfts);
      getAssetDetails(assets);
      setIsLoading(false);
    };
    if (saleIncentiveCongratsModalOpen.open) {
      fetch();
    }
  }, [saleIncentiveCongratsModalOpen]);

  const getAssetDetails = async (assets: SpecificBidAsset[]) => {
    // Assume the input is an array of assets
    const assetDetails = assets.map(asset => {
      const assetDetail: AssetDetail = {
        mint: asset.mint,
        decimals: outbidSale.assets[asset.mint.toBase58()]?.decimals || 0,
        symbol: outbidSale.assets[asset.mint.toBase58()]?.symbol,
        amount: asset.amount,
        img: outbidSale.assets[asset.mint.toBase58()]?.symbol,
      };
      return assetDetail;
    });
    setAssets(assetDetails);
  };

  const getNftDetails = async (nfts: DigitalAsset[]) => {
    const nftsImages = [];
    for (const nft of nfts) {
      const digitalAsset = await fetchDigitalAsset(umi, nft.mint);
      const res = await fetch(digitalAsset.metadata.uri);
      if (res.ok) {
        const data = await res.json();
        if (data.image) {
          nftsImages.push(data.image);
        }
      } else {
        const msg = "Unable to Preview NFT";
        dispatch(sendToast({ type: "tx-fail", info: { msg: msg, toastID: "" + new Date().getTime() } }));
        console.error("Fetch error: ", res.statusText);
      }
    }
    setNftImages(nftsImages);
  };

  return (
    <>
      <CustomModal
        isOpen={saleIncentiveCongratsModalOpen.open}
        onClose={() => {
          setNftImages([]);
          setSaleIncentiveCongratsModalOpen({
            open: false,
            incentives: [],
          });
        }}
        modalSize="sm"
      >
        <section className="sectionModalHeader">
          <h2>{i18("Outbid Reward", "sale.outbid.reward")}</h2>
        </section>

        <div style={{ fontSize: "1em" }}>
          <p style={{ fontSize: "1em" }}>
            {i18("Congratulations! You've won the following rewards:", "sale.outbid.congrats")}
          </p>
          <div style={{ display: "flex", placeContent: "space-evenly" }}>
            {isLoading && <CustomLoader />}
            {assets.map((asset: AssetDetail) => (
              <div className="inlineFlexbox" key={asset.mint.toBase58()}>
                <span style={{ fontSize: "2em", marginRight: ".25em" }}>
                  {BigNumber(asset.amount).div(Math.pow(10, asset.decimals)).toString(10)}
                </span>
                <img className="tokenIcon" src={imgSanitize(asset.img)} alt={asset.symbol} />
              </div>
            ))}
            {nftImages.map((nftImage: uri) => (
              <img key={nftImage} src={nftImage} alt={i18("NFT Image", "sale.nft.image")} />
            ))}
          </div>
        </div>

        <div style={{ textAlign: "right", marginTop: "1.5em" }}>
          <Button
            title={i18("Share on X", "sale.share.x")}
            style={{ padding: "1em", margin: ".5em", marginTop: "1em" }}
            btnColor="gradient"
            onClick={() => {
              let winString = assets
                .map(asset => {
                  return `${BigNumber(asset.amount).div(Math.pow(10, asset.decimals)).toString(10)} ${asset.symbol}`;
                })
                .join(", ");
              winString += nftImages.length > 0 ? i18("an NFT", "sale.an.nft") : "";
              window.open(
                "https://twitter.com/intent/tweet?text=" +
                i18(
                  `I won ${winString} in the ${outbidSale.name} campaign on @outbid_launch where every bid is a winner%21`,
                  "sale.share.x.post",
                  { prize: winString, project: outbidSale.name }
                ).replace(/(%20|\s)+/g, "+")
              );
            }}
            disabled={isLoading}
          >
            {i18("Share on", "sale.share.on")}
            <IcnTwitter style={{ width: "1em", height: "1em" }} />
          </Button>
        </div>
      </CustomModal>
    </>
  );
}

export default SaleIncentiveCongratsModal;

// Helper =============================================================================================================

type uri = string;
type AssetDetail = {
  mint: PublicKey;
  decimals: number;
  symbol: string;
  amount: BigNumber;
  img: uri;
};
